import { CheckCircle, Sparkles } from "lucide-react";
import { usePricesInsertion } from "@/api/cms";
import { useAuth } from "@/context/auth/AuthContext";
import { usePuckEditorLocale } from "@/context/cms-puck/PuckEditorLocaleContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

type InsertionPrice = {
  id: string;
  name?: Record<string, string>;
  description?: Record<string, string>;
  priceDescriptionFromStripe?: { price?: { unit_amount?: number | string } };
};

/** Valeur dans la langue d'édition, sinon la première langue renseignée. */
function localized(value: Record<string, string> | undefined, locale: string) {
  if (!value) return "";
  return value[locale] || Object.values(value).find(Boolean) || "";
}

/**
 * Aperçu éditeur du bloc `ExpertiseOffers` : titre, entrée « Vérifier une
 * œuvre » (non cliquable ici) et une carte par tarif d'insertion Stripe, dans
 * les deux variantes du front selon `hideNewArtworkAutheticationForm`.
 * PARITÉ front : PuckExpertiseOffers + ExpertiseCard.
 */
export function ExpertiseOffersBlock({ title }: { title?: React.ReactNode }) {
  const cmsNew = useCmsNew();
  const locale = usePuckEditorLocale() ?? "fr";
  const { instanceData } = useAuth();
  const { data, isLoading } = usePricesInsertion();
  const prices = (data ?? []) as InsertionPrice[];
  const priceLabel = (price: InsertionPrice) =>
    `${Number(price.priceDescriptionFromStripe?.price?.unit_amount ?? 0) / 100} €`;
  const buttonClass =
    "w-full rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground";

  return (
    <div className="flex w-full flex-col items-center gap-16 px-5 py-8 text-center md:px-10">
      {title ? (
        <p className="w-full text-2xl font-semibold md:w-2/3 md:text-4xl lg:w-1/2 xl:w-2/5">
          {title}
        </p>
      ) : null}
      {!instanceData?.hideArtMatchFront ? (
        <span className="inline-flex h-12 items-center gap-3 rounded-full border border-current px-5 text-sm font-medium shadow-lg">
          <Sparkles className="size-8 shrink-0" />
          {cmsNew(
            "cms.new.blocks.expertiseOffers.verify",
            "Vérifier une œuvre",
          )}
        </span>
      ) : null}
      <div className="mx-auto flex w-full max-w-screen-xl flex-col flex-wrap justify-center gap-5 md:flex-row">
        {isLoading ? null : prices.length === 0 ? (
          <p className="text-sm opacity-60">
            {cmsNew(
              "cms.new.blocks.expertiseOffers.empty",
              "Aucun tarif d'insertion configuré dans Stripe.",
            )}
          </p>
        ) : (
          prices.map((price) =>
            !instanceData?.hideNewArtworkAutheticationForm ? (
              <div
                key={price.id}
                className="cms-page-border flex h-[240px] w-full flex-col items-center justify-between gap-4 border p-6 shadow-lg md:w-[32%]"
              >
                <div className="flex flex-col items-center">
                  <h2 className="p-1 text-3xl font-semibold">
                    {localized(price.name, locale)}
                  </h2>
                  <p className="text-2xl">{priceLabel(price)}</p>
                </div>
                <span className={buttonClass}>
                  {cmsNew(
                    "cms.new.blocks.expertiseOffers.cardButton",
                    "Commencer l'authentification",
                  )}
                </span>
              </div>
            ) : (
              <div
                key={price.id}
                className="cms-page-border-strong cms-page-surface flex w-full flex-col items-center gap-6 rounded-2xl border p-8 shadow-lg md:w-[32%]"
              >
                <div className="cms-page-border rounded-xl border p-1">
                  <span className="cms-page-surface-elevated inline-flex items-center rounded-md border px-2.5 py-0.5 text-sm font-medium">
                    {localized(price.name, locale)}
                  </span>
                </div>
                <p className="text-5xl font-semibold">{priceLabel(price)}</p>
                {localized(price.description, locale) ? (
                  <div className="cms-page-border cms-page-text-muted flex items-start gap-4 border-y py-8">
                    <CheckCircle size={32} />
                    <p className="text-left text-base font-normal">
                      {localized(price.description, locale)}
                    </p>
                  </div>
                ) : null}
                <span className={buttonClass}>
                  {cmsNew(
                    "cms.new.blocks.expertiseOffers.payButton",
                    "Payer l'insertion",
                  )}
                </span>
              </div>
            ),
          )
        )}
      </div>
    </div>
  );
}
